import type { Submission } from './submission';

/**
 * Leaderboard client.
 *
 * The endpoint is injected at build time via `VITE_LEADERBOARD_URL`. With none
 * configured every call resolves to a "disabled" result and the UI simply hides
 * the board — the game must stay fully playable with no backend at all, which is
 * the whole point of the no-login format.
 */
const ENDPOINT = (import.meta.env.VITE_LEADERBOARD_URL as string | undefined) ?? '';

export interface LeaderboardEntry {
  rank: number;
  name: string;
  timeMs: number;
  livery: string;
  /** Present only for the requesting player's own row. */
  isYou?: boolean;
}

export interface LeaderboardPage {
  status: 'ok' | 'disabled' | 'error';
  entries: LeaderboardEntry[];
  yourRank: number | null;
  total: number;
  message?: string;
}

const DISABLED: LeaderboardPage = {
  status: 'disabled',
  entries: [],
  yourRank: null,
  total: 0,
};

export function leaderboardEnabled(): boolean {
  return ENDPOINT.length > 0;
}

/** Stable anonymous id so a player can find their own row without an account. */
export function playerId(): string {
  const KEY = 'karting-hotlap:player-id';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
    return id;
  } catch {
    return 'anonymous';
  }
}

export function playerName(): string {
  try {
    return localStorage.getItem('karting-hotlap:player-name') ?? '';
  } catch {
    return '';
  }
}

export function setPlayerName(name: string): void {
  try {
    localStorage.setItem('karting-hotlap:player-name', name.slice(0, 20));
  } catch {
    /* ignore */
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export async function call<T>(path: string, init?: RequestInit): Promise<T | null> {
  if (!ENDPOINT) return null;
  // A leaderboard is optional garnish, not the game: if the endpoint accepts
  // the connection and then just sits there, an unbounded fetch would leave
  // the panel on "Loading…" forever with no way out. 8s is plenty for a JSON
  // reply and short enough that a stall still clears within one visit.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(`${ENDPOINT}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    // The server is out of our control, so a 200 with a body that doesn't
    // match what we asked for is a real possibility, not paranoia. Only the
    // fields callers actually dereference are worth checking here — a
    // leaderboard page is walked as a list right after this returns, so a
    // missing/wrong-typed `entries` must be caught now rather than throwing
    // inside a render function. Anything that fails the check is treated the
    // same as a network failure below, so the UI falls back the same way.
    if (!isRecord(data) || (path.startsWith('/leaderboard') && !Array.isArray(data.entries))) {
      return null;
    }
    return data as T;
  } catch {
    // Network failure, a non-JSON body, and the timeout abort above all land
    // here — to the caller they're the same thing: the leaderboard didn't
    // answer.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchLeaderboard(
  trackKey: string,
  mode: string,
  limit = 20,
): Promise<LeaderboardPage> {
  if (!ENDPOINT) return DISABLED;
  const params = new URLSearchParams({ track: trackKey, mode, limit: String(limit), player: playerId() });
  const page = await call<LeaderboardPage>(`/leaderboard?${params}`);
  return page ?? { ...DISABLED, status: 'error', message: 'Leaderboard unreachable' };
}

export interface RivalLap {
  name: string;
  timeMs: number;
  livery: string;
  traceB64: string;
  rank: number;
}

/**
 * A rival's actual lap, returned as the input trace they drove.
 *
 * The client re-simulates it locally rather than the server sending positions —
 * same mechanism the leaderboard uses to verify times, so a rival ghost is
 * guaranteed to be a lap that really happened under the same physics.
 *
 * Defaults to the lap immediately quicker than yours, which is a far better
 * opponent than the day's runaway leader.
 */
export async function fetchRivalLap(
  trackKey: string,
  mode: string,
  opts: { rank?: number; nearby?: boolean } = { nearby: true },
): Promise<RivalLap | null> {
  if (!ENDPOINT) return null;
  const params = new URLSearchParams({
    track: trackKey,
    mode,
    player: playerId(),
    ...(opts.nearby ? { nearby: '1' } : { rank: String(opts.rank ?? 1) }),
  });
  const res = await call<RivalLap & { status: string }>(`/ghost?${params}`);
  return res && res.status === 'ok' ? res : null;
}

export async function submitRun(
  submission: Submission,
): Promise<{ accepted: boolean; rank?: number; message?: string }> {
  if (!ENDPOINT) return { accepted: false, message: 'No leaderboard configured' };
  const body = JSON.stringify({ ...submission, player: playerId(), name: playerName() });
  const res = await call<{ accepted: boolean; rank?: number; message?: string }>('/submit', {
    method: 'POST',
    body,
  });
  return res ?? { accepted: false, message: 'Submission failed' };
}
